/**
 * The findings queue's arrangement, pure so it is pinned by tests: which
 * findings a stage and the filters admit, how they are grouped and ordered,
 * and the small labels a row shows (age, due date).
 *
 * @module components/regulation/finding/FindingsQueue.logic
 */
import type { PolicyFinding } from "../../../state/governance";
import {
  BASIS_LABEL,
  SEVERITY_LABEL,
  SEVERITY_ORDER,
  type FindingBasisKind,
  type FindingItem,
  type Severity,
} from "./findingItem";

export type FindingStage = "open" | "resolved" | "dismissed" | "all";
export type FindingGrouping = "document" | "severity" | "basis" | "assignee" | "none";
export type FindingOrdering = "severity" | "newest" | "oldest" | "due";
export type FindingRowProperty = "state" | "basis" | "document" | "assignee" | "due" | "age";

export const FINDING_STAGES: ReadonlyArray<readonly [FindingStage, string]> = [
  ["open", "Open"],
  ["resolved", "Resolved"],
  ["dismissed", "Dismissed"],
  ["all", "All"],
];
export const FINDING_GROUPINGS: ReadonlyArray<readonly [FindingGrouping, string]> = [
  ["document", "Document"],
  ["severity", "Severity"],
  ["basis", "Basis"],
  ["assignee", "Assignee"],
  ["none", "No grouping"],
];
export const FINDING_ORDERINGS: ReadonlyArray<readonly [FindingOrdering, string]> = [
  ["severity", "Severity"],
  ["newest", "Newest"],
  ["oldest", "Oldest"],
  ["due", "Due date"],
];
export const FINDING_ROW_PROPERTIES: ReadonlyArray<readonly [FindingRowProperty, string]> = [
  ["state", "State"],
  ["basis", "Basis"],
  ["document", "Document"],
  ["assignee", "Assignee"],
  ["due", "Due date"],
  ["age", "Age"],
];

export interface FindingQueueView {
  readonly stage: FindingStage;
  readonly query: string;
  readonly severities: ReadonlyArray<Severity>;
  readonly bases: ReadonlyArray<FindingBasisKind>;
  /** Document keys (`documentKey`). */
  readonly documents: ReadonlyArray<string>;
  readonly grouping: FindingGrouping;
  readonly ordering: FindingOrdering;
  readonly properties: ReadonlyArray<FindingRowProperty>;
  readonly collapsed: ReadonlyArray<string>;
}

export const EMPTY_FINDING_QUEUE: FindingQueueView = {
  stage: "open",
  query: "",
  severities: [],
  bases: [],
  documents: [],
  grouping: "document",
  ordering: "severity",
  properties: ["state", "basis", "document", "assignee", "due", "age"],
  collapsed: [],
};

/** Missing documents share one group: they have no document yet. */
export const MISSING_DOCUMENTS_KEY = "missing-documents";
export const documentKey = (item: Pick<FindingItem, "policyId">) => item.policyId ?? MISSING_DOCUMENTS_KEY;

export const matchesFindingStage = (item: Pick<FindingItem, "status">, stage: FindingStage) =>
  stage === "all" || item.status === stage;

/** Active filters, the search included, for the Filter button's badge. */
export const activeFilterCount = (view: FindingQueueView) =>
  view.severities.length + view.bases.length + view.documents.length + (view.query.trim() ? 1 : 0);

/** The filters without the stage: what the stage tabs count over. */
export function filterFindingItems(
  items: ReadonlyArray<FindingItem>,
  view: FindingQueueView,
  personName: (id: string) => string | null = () => null,
): FindingItem[] {
  const query = view.query.trim().toLocaleLowerCase();
  return items.filter((item) => {
    if (view.severities.length > 0 && (item.severity === null || !view.severities.includes(item.severity))) return false;
    if (view.bases.length > 0 && !view.bases.includes(item.basis.kind)) return false;
    if (view.documents.length > 0 && !view.documents.includes(documentKey(item))) return false;
    if (!query) return true;
    return [
      item.title,
      item.gap,
      item.documentName,
      item.basis.label,
      item.raisedBy,
      item.assigneeId ? personName(item.assigneeId) : null,
    ]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase()
      .includes(query);
  });
}

const STATUS_RANK: Readonly<Record<FindingItem["status"], number>> = { open: 0, resolved: 1, dismissed: 2 };
const severityRank = (severity: Severity | null) => (severity === null ? SEVERITY_ORDER.length : SEVERITY_ORDER.indexOf(severity));
const dueRank = (item: FindingItem) => (item.dueDate ? Date.parse(`${item.dueDate}T00:00:00Z`) : Number.POSITIVE_INFINITY);

/** Open before decided, then the chosen ordering; ties fall to the id so the order is stable. */
export function compareFindingItems(ordering: FindingOrdering) {
  return (left: FindingItem, right: FindingItem): number =>
    STATUS_RANK[left.status] - STATUS_RANK[right.status] ||
    (ordering === "newest"
      ? right.raisedAt - left.raisedAt
      : ordering === "oldest"
        ? left.raisedAt - right.raisedAt
        : ordering === "due"
          ? dueRank(left) - dueRank(right) || severityRank(left.severity) - severityRank(right.severity)
          : severityRank(left.severity) - severityRank(right.severity) || right.raisedAt - left.raisedAt) ||
    left.id.localeCompare(right.id);
}

export interface FindingGroup {
  readonly key: string;
  readonly label: string;
  readonly items: ReadonlyArray<FindingItem>;
}

/** The group a row sits in under a grouping. */
export function groupKeyOf(item: FindingItem, grouping: FindingGrouping): string {
  switch (grouping) {
    case "document":
      return documentKey(item);
    case "severity":
      return item.severity ?? "unrated";
    case "basis":
      return item.basis.kind;
    case "assignee":
      return item.assigneeId ?? "unassigned";
    case "none":
      return "all";
  }
}

/**
 * The rows of a stage, grouped and ordered. Groups follow their worst row: a
 * document with a critical open finding comes before one with only low ones;
 * severity and basis groups keep their natural order; the unassigned and
 * missing-document groups come last.
 */
export function groupFindingItems(
  items: ReadonlyArray<FindingItem>,
  view: FindingQueueView,
  personName: (id: string) => string | null = () => null,
): FindingGroup[] {
  const rows = items.filter((item) => matchesFindingStage(item, view.stage)).sort(compareFindingItems(view.ordering));
  const groups = new Map<string, FindingItem[]>();
  for (const item of rows) {
    const key = groupKeyOf(item, view.grouping);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  const label = (key: string, first: FindingItem): string => {
    switch (view.grouping) {
      case "document":
        return key === MISSING_DOCUMENTS_KEY ? "Missing documents" : first.documentName;
      case "severity":
        return key === "unrated" ? "No severity" : SEVERITY_LABEL[key as Severity];
      case "basis":
        return BASIS_LABEL[key as FindingBasisKind];
      case "assignee":
        return key === "unassigned" ? "Unassigned" : (personName(key) ?? "Former member");
      case "none":
        return "All";
    }
  };
  const rank = (key: string): number => {
    switch (view.grouping) {
      case "severity":
        return key === "unrated" ? SEVERITY_ORDER.length : SEVERITY_ORDER.indexOf(key as Severity);
      case "basis":
        return ["regulation", "control", "missing"].indexOf(key);
      case "assignee":
        return key === "unassigned" ? 1 : 0;
      case "document":
        return key === MISSING_DOCUMENTS_KEY ? 1 : 0;
      case "none":
        return 0;
    }
  };
  return [...groups.entries()]
    .map(([key, groupItems]) => ({ key, label: label(key, groupItems[0]!), items: groupItems }))
    .sort((left, right) => {
      const byRank = rank(left.key) - rank(right.key);
      if (byRank !== 0 || (view.grouping !== "document" && view.grouping !== "assignee")) return byRank;
      // Documents and people: the one with the worst first row leads, then by name.
      return compareFindingItems(view.ordering)(left.items[0]!, right.items[0]!) || left.label.localeCompare(right.label);
    });
}

/** Counts per stage tab, after the filters. */
export function stageCounts(items: ReadonlyArray<FindingItem>): Readonly<Record<FindingStage, number>> {
  return {
    open: items.filter((item) => item.status === "open").length,
    resolved: items.filter((item) => item.status === "resolved").length,
    dismissed: items.filter((item) => item.status === "dismissed").length,
    all: items.length,
  };
}

/** "now", "5m", "3h", "4d", "2w", "3mo", "1y" — Linear's age column. */
export function relativeAge(at: number, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - at) / 60_000));
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.round(days / 7)}w`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
}

const DAY = 86_400_000;

/** The calendar day of `at` in local time, as `YYYY-MM-DD`. */
export const localDay = (at: number = Date.now()): string => {
  const date = new Date(at);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** "Today", "Tomorrow", "3d overdue", "31 Oct". Days are compared as calendar days. */
export function dueLabel(dueDate: string, now: number = Date.now()): { readonly text: string; readonly overdue: boolean } {
  const due = Date.parse(`${dueDate}T00:00:00Z`);
  const today = Date.parse(`${localDay(now)}T00:00:00Z`);
  const days = Math.round((due - today) / DAY);
  if (Number.isNaN(days)) return { text: dueDate, overdue: false };
  const text =
    days < 0
      ? `${-days}d overdue`
      : days === 0
        ? "Today"
        : days === 1
          ? "Tomorrow"
          : new Date(due).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  return { text, overdue: days < 0 };
}

/** Preset due dates for the picker, as days. */
export function duePresets(now: number = Date.now()): ReadonlyArray<{ readonly label: string; readonly day: string }> {
  const date = new Date(now);
  const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getTime();
  return [
    { label: "Tomorrow", day: localDay(now + DAY) },
    { label: "In one week", day: localDay(now + 7 * DAY) },
    { label: "In two weeks", day: localDay(now + 14 * DAY) },
    { label: "End of this month", day: localDay(endOfMonth) },
    { label: "In one month", day: localDay(now + 30 * DAY) },
  ];
}

/** The next open finding after `id`, in the queue's order, wrapping round. */
export function nextOpenFindingItem(ordered: ReadonlyArray<FindingItem>, id: string): FindingItem | null {
  const index = ordered.findIndex((item) => item.id === id);
  const rest = [...ordered.slice(index + 1), ...ordered.slice(0, Math.max(0, index))];
  return rest.find((item) => item.status === "open" && item.id !== id) ?? null;
}

/* ------------------------------------------------------------------ */
/* Regulatory findings alone — Home's open-findings list reads these.  */
/* ------------------------------------------------------------------ */

export interface FindingFilters {
  readonly stage: "open" | "resolved" | "closed" | "all";
  readonly query: string;
  readonly severity: string;
}
export const EMPTY_FINDING_FILTERS: FindingFilters = { stage: "open", query: "", severity: "all" };
const PRIORITY: Readonly<Record<string, number>> = { critical: 4, high: 3, medium: 2, low: 1 };

/** Stable priority order, with the newest evidence breaking ties. Never mutates the live snapshot. */
export function filterFindings(
  findings: ReadonlyArray<PolicyFinding>,
  filters: FindingFilters,
  documentNames: ReadonlyMap<string, string> = new Map(),
): PolicyFinding[] {
  const query = filters.query.trim().toLocaleLowerCase();
  return findings
    .filter(
      (finding) =>
        (filters.stage === "all" || finding.status === filters.stage) &&
        (filters.severity === "all" || finding.severity === filters.severity) &&
        (!query ||
          [finding.title, finding.summary, finding.assessment, finding.jurisdictionSlug, finding.sourceTitle, documentNames.get(finding.policyId)]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase()
            .includes(query)),
    )
    .sort(
      (a, b) =>
        Number(b.status === "open") - Number(a.status === "open") ||
        (PRIORITY[b.severity ?? ""] ?? 0) - (PRIORITY[a.severity ?? ""] ?? 0) ||
        (Date.parse(b.detectedAt) || 0) - (Date.parse(a.detectedAt) || 0) ||
        a.id.localeCompare(b.id),
    );
}
